/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { useCountryData } from 'msp-integrations'
import { useIntl } from 'react-intl'

import { DEFAULT_SITE_TITLE } from '../../constants'

export const useSeo = ({ page, title, description }) => {
  const { formatMessage } = useIntl()
  const seoTitle =
    page.found && page?.meta?.title ? page?.meta?.title : formatMessage(title)
  const seoDescription =
    page.found && page?.meta?.description
      ? page?.meta?.description
      : formatMessage(description)
  const seoKeywords =
    page.found && page?.meta?.keywords ? page?.meta?.keywords : ''

  return { seoTitle, seoDescription, seoKeywords }
}

const Seo = ({
  title,
  description,
  keywords,
  canonicals = [],
  canonicalPath,
  noIndex,
  children,
  ...props
}: any) => {
  const fullTitle = title
    ? `${title} | ${DEFAULT_SITE_TITLE}`
    : DEFAULT_SITE_TITLE
  const i18n = useCountryData()
  if (canonicals.length === 0 && canonicalPath) {
    canonicals = i18n?.countries
      ?.map((c) =>
        c.locales.map((l) => {
          return {
            locale: l?.locale?.replace('_', '-'),
            url: `${l?.link}${canonicalPath}`
          }
        })
      )
      .flat()
  }

  return (
    <Helmet {...props}>
      <title>{fullTitle}</title>
      {description && <meta name='description' content={description} />}
      {keywords && <meta name='keywords' content={keywords} />}
      {noIndex && <meta name='robots' content='noindex' />}
      {canonicals.map((canonical) => (
        <link
          rel='alternate'
          hrefLang={canonical.locale}
          href={canonical.url}
          key={canonical.locale}
        />
      ))}
      {children}
    </Helmet>
  )
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  canonicals: PropTypes.array,
  canonicalPath: PropTypes.string,
  noIndex: PropTypes.bool,
  children: PropTypes.node,
  htmlAttributes: PropTypes.object
}

export default Seo
